import { useToasts } from '@mnd-frontend/ui';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DocumentUploader from '../../../../components/MediaUploaders/DocumentUploader';
import { useCloudinaryDocumentUpload } from '../../../../hooks/useCloudinaryDocumentUpload';
import { UploadQueue } from './utils/UploadQueue';

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  > *:first-child {
    flex: 1;
  }
  > *:nth-child(2) {
    margin-top: 0.5rem;
  }
`;

const InnerDocumentUploaderCell = ({
  setPendingFiles,
  refetch,
  hidden = false,
}: {
  setPendingFiles: React.Dispatch<React.SetStateAction<number>>;
  refetch: () => void;
  hidden?: boolean;
}) => {
  const { showFailureToast } = useToasts();
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const uploadQueueRef = useRef<UploadQueue | null>(null);
  const [t] = useTranslation();

  const [uploadDocument] = useCloudinaryDocumentUpload({
    onError: (error?: string) => showFailureToast(error ?? t('common_general_error')),
    onCompleted: response => {
      const error = response.documentSyncCloudinaryUpload?.error;
      if (error) {
        showFailureToast(error.message || t('common_general_error'));
      }
    },
  });

  const getUploadQueue = () => {
    if (uploadQueueRef.current) return uploadQueueRef.current;

    uploadQueueRef.current = new UploadQueue();
    return uploadQueueRef.current;
  };

  useEffect(() => {
    getUploadQueue().updateFunctionPointers({
      upload: uploadDocument,
      onStarted: () => setUploadingDocument(true),
      onCompleted: () => setUploadingDocument(false),
      onFileUploaded: refetch,
      onCountChanged: count => {
        setPendingFiles(count);
      },
    });
  });

  useEffect(
    () => () => {
      getUploadQueue().destroy();
    },
    [setPendingFiles],
  );

  return (
    <StyledDiv style={hidden ? { display: 'none' } : undefined}>
      <DocumentUploader
        loading={uploadingDocument}
        action={(file: Blob) => {
          getUploadQueue().queue([file]);
        }}
      />
    </StyledDiv>
  );
};

export const DocumentUploaderCell = React.memo(InnerDocumentUploaderCell);
